'use client';

import {type FC, useCallback, useState} from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import {Expand} from 'lucide-react';

import {
	Container,
	type ContainerProps,
	type ContainerSpacing,
} from '@/modules/foundation/components/container';
import {ArrowLeftIcon} from '@/modules/foundation/components/icons/arrow-left-icon';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import ImageFill from '@/modules/foundation/components/image/ImageFill';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import {usePrefersReducedMotion} from '@/modules/foundation/hooks/usePrefersReducedMotion';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import {useDotButton} from './hooks/useDotButton';
import {usePrevNextButtons} from './hooks/usePrevNextButtons';
import {Lightbox} from './lightbox';

type Props = {
	images: KeyedArray<ImageWeb>;
	aspectRatio?: '1:1' | '4:3' | '3:2' | '16:9';
	width?: ContainerProps['width'];
	spacingTop?: ContainerSpacing;
	spacingBottom?: ContainerSpacing;
};

export const Gallery: FC<Props> = ({
	spacingBottom,
	spacingTop,
	images,
	aspectRatio = '4:3',
	width,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const handleOpenFullscreenClick = useCallback(() => {
		setIsOpen(true);
	}, []);

	const prefersReducedMotion = usePrefersReducedMotion();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: images.length > 1,
		duration: prefersReducedMotion ? 0 : 20,
	});
	const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);
	const {prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick} =
		usePrevNextButtons(emblaApi);

	return (
		<>
			<Container width={width} spacingBottom={spacingBottom} spacingTop={spacingTop}>
				<div
					className={clsx([
						'relative flex',
						'overflow-hidden',
						'rounded-sm',
						aspectRatio === '1:1' && 'aspect-square',
						aspectRatio === '4:3' && 'aspect-[calc(4/3)]',
						aspectRatio === '3:2' && 'aspect-[calc(3/2)]',
						aspectRatio === '16:9' && 'aspect-video',
					])}
				>
					<div ref={emblaRef} className="flex shrink-0 grow overflow-hidden">
						<div className="flex grow touch-pan-y touch-pinch-zoom">
							{images.map((image) => (
								<div
									key={image._key}
									className="relative shrink-0 grow-0 basis-full"
								>
									<ImageFill
										{...image.image}
										alt={image.altText ?? ''}
										className="object-cover"
									/>
								</div>
							))}
						</div>
					</div>

					{images.length > 1 && (
						<>
							<button
								type="button"
								onClick={onPrevButtonClick}
								disabled={prevBtnDisabled}
								aria-label="Forrige bilde"
								className={clsx(
									'absolute',
									'top-1/2',
									'left-0',
									'-translate-y-1/2',
									'p-2',
									'disabled:opacity-50',
									'bg-black/70',
									'rounded-sm',
									'cursor-pointer',
									'disabled:pointer-events-none',
								)}
							>
								<ArrowLeftIcon size={32} />
							</button>

							<button
								type="button"
								onClick={onNextButtonClick}
								disabled={nextBtnDisabled}
								aria-label="Neste bilde"
								className={clsx(
									'absolute',
									'top-1/2',
									'right-0',
									'-translate-y-1/2',
									'p-2',
									'disabled:opacity-50',
									'bg-black/70',
									'rounded-sm',
									'cursor-pointer',
									'disabled:pointer-events-none',
								)}
							>
								<ArrowRightIcon size={32} />
							</button>

							<div className="absolute inset-x-0 bottom-4 flex flex-wrap items-center justify-center gap-2">
								{scrollSnaps.map((_, index) => (
									<button
										type="button"
										key={index}
										onClick={() => onDotButtonClick(index)}
										aria-label={`Bilde ${index + 1}`}
										className={clsx(
											"relative h-1 w-10 bg-black opacity-60 before:absolute before:inset-[-8px] before:content-[''] hover:opacity-100 focus:opacity-100 disabled:opacity-30",
											selectedIndex === index ? 'opacity-100' : '',
										)}
									/>
								))}
							</div>
						</>
					)}
					<div className={clsx('absolute', 'right-1', 'top-1', 'z-10')}>
						<button
							onClick={handleOpenFullscreenClick}
							className={clsx(
								'bg-black/70',
								'p-2',
								'rounded-sm',
								'text-white',
								'cursor-pointer',
							)}
							aria-label="Åpne bildegalleri i fullskjerm"
						>
							<Expand size={32} aria-hidden />
						</button>
					</div>
				</div>
			</Container>
			<Lightbox
				setIsOpen={setIsOpen}
				startIndex={selectedIndex}
				isOpen={isOpen}
				images={images}
			/>
		</>
	);
};
